import { useGetSuperAdminStatsFranchises } from '@agos/api-client/platform'
import { T, formatBytes, formatCents, formatCount, tt } from '@agos/shared'
import { ShoppingBag, Users, Wallet } from 'lucide-react'
import { useMemo } from 'react'
import { PageHeader, type Column, DataTable, ReadFailed, Spinner, StatCard } from '@agos/ui'

import {
  activityNote,
  activityState,
  franchiseStatsOf,
  lifetimeNote,
  showsFigures,
  statsTotals,
  type FranchiseStats,
} from './franchise-stats'

/**
 * Per-franchise figures - `SAAS.md` §3.12.
 *
 * Two of the three franchises have no orders and no customers on this data, so the ordinary case for
 * this table is mostly zeros - and a zero row is drawn rather than filtered out, because a franchise
 * that has stopped selling is exactly what somebody opens this page to find.
 *
 * **And that sentence was the camouflage.** The endpoint answers fourteen fields and this table drew
 * four; two of the dropped ones were the ones that make a zero mean something:
 *
 * - **`error`** - `PlatformStatsService` reports an unreachable tenant *on the row* and leaves its
 *   figures at zero, deliberately, so a dashboard stays usable during an incident. Unread, a
 *   franchise that was **down** rendered identically to one with **no sales**.
 * - **`lastOrderAt`** - the field that tells *stopped* from *quiet*. `pizzamajstro` shows 0 orders
 *   and 0,00 € beside **2 462 customers** because its newest order is seven weeks old.
 */
export function StatsPage() {
  const { data, isLoading, isError } = useGetSuperAdminStatsFranchises()
  const rows = useMemo(() => franchiseStatsOf(data), [data])
  // Read once per render, after the memo, so nothing non-deterministic sits inside it.
  const now = new Date()
  const totals = useMemo(() => statsTotals(rows), [rows])
  const unreadableNote = `${formatCount(totals.unreadable)} ${tt('nedostupných nezapočítaných')}`

  /**
   * The columns, sortable where a number makes sorting mean something - §3.92.
   *
   * The name column is not sortable on purpose: it carries the three sub-lines as well, and sorting
   * a list of three franchises alphabetically answers nothing the eye does not. The figures are the
   * reason this page exists and *who sells most* was unanswerable before.
   *
   * **A zero the screen cannot vouch for is still not shown as one.** `showsFigures` gates every
   * numeric cell exactly as it did in the hand-rolled table: on an unreachable row every figure is
   * the initialised zero rather than a measurement. `sortValue` returns null for those, so they sort
   * to one end instead of being read as genuine noughts.
   */
  /*
   * Not memoised, deliberately. `now` is read once per render - the sub-lines say *how long since
   * the last order* - so a `useMemo` keyed on it recomputes every render anyway, which lint says out
   * loud, and keying it on `[]` instead would freeze the clock for the life of the mount. Four
   * column objects are cheaper than either, and `DataTable` does not need referential stability.
   */
  const columns: Column<FranchiseStats>[] = [
      {
        key: 'name',
        header: tt('Franšíza'),
        render: (row) => (
          <div>
            {row.name}
            {row.packageSlug !== null && (
              <span className="text-[10px] text-muted-foreground" data-stat-package>
                {' '}
                &middot; {row.packageSlug}
              </span>
            )}
            {/* The sentence that makes the row legible, said for every state - a row with no note
              * reads as a row nobody checked. */}
            <p
              className={`text-[10px] ${showsFigures(row) ? 'text-muted-foreground' : 'font-semibold text-red-500'}`}
              data-stat-note
            >
              {activityNote(row, now)}
            </p>
            {lifetimeNote(row) !== '' && (
              <p className="text-[10px] text-muted-foreground" data-stat-lifetime>
                {lifetimeNote(row)}
              </p>
            )}
            {/* §3.42 - the three shorter windows and the database size, all four answered by the
              * endpoint. Gated on `showsFigures` like the cells, and for the same reason. */}
            {showsFigures(row) && (
              <p className="text-[10px] text-muted-foreground" data-stat-windows>
                <T>Dnes</T> <span data-orders-today>{formatCount(row.ordersToday)}</span>
                {' / '}
                <span data-revenue-today>{formatCents(row.revenueTodayCents)}</span>
                {' · '}
                <T>7 dní</T> <span data-orders-week>{formatCount(row.ordersWeek)}</span>
                {row.dbSizeBytes > 0 && (
                  <span data-db-size>
                    {' · '}
                    {formatBytes(row.dbSizeBytes)}
                  </span>
                )}
              </p>
            )}
          </div>
        ),
      },
      {
        key: 'ordersMonth',
        header: tt('Objednávky (30 dní)'),
        className: 'text-right',
        headerClassName: 'text-right',
        sortable: true,
        sortValue: (row) => (showsFigures(row) ? row.ordersMonth : null),
        render: (row) =>
          showsFigures(row) ? (
            <span data-orders-month>{formatCount(row.ordersMonth)}</span>
          ) : (
            <span className="text-red-500" data-stat-unreadable>
              <T>Nedostupné</T>
            </span>
          ),
      },
      {
        key: 'revenueMonth',
        header: tt('Tržby (30 dní)'),
        className: 'text-right',
        headerClassName: 'text-right',
        sortable: true,
        sortValue: (row) => (showsFigures(row) ? row.revenueMonthCents : null),
        render: (row) =>
          showsFigures(row) ? <span data-revenue-month>{formatCents(row.revenueMonthCents)}</span> : null,
      },
      {
        key: 'customers',
        header: tt('Zákazníci'),
        className: 'text-right',
        headerClassName: 'text-right',
        sortable: true,
        sortValue: (row) => (showsFigures(row) ? row.totalCustomers : null),
        render: (row) =>
          showsFigures(row) ? <span data-total-customers>{formatCount(row.totalCustomers)}</span> : null,
      },
  ]

  return (
    <main data-franchise-stats>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      <PageHeader title={tt('Štatistiky')} description={tt('Objednávky, tržby a zákazníci po franšízach.')} />
      {/* §3.82 - a screen that cannot load its data must not render its empty state.
        * Discarding `isLoading` and `isError` meant *"Žiadne prevádzky"* appeared while
        * the request was in flight and again when it failed - so a broken load read as a quiet
        * period, which is §3.52's argument applied to the six screens it did not reach. */}
      {isError && (
        <ReadFailed marker="stats-error"><T>Štatistiky franšíz sa nepodarilo načítať.</T></ReadFailed>
      )}
      {isLoading && (
        <div className="py-10 flex justify-center" data-stats-loading>
          <Spinner />
        </div>
      )}


      {/* §3.92 - the cross-franchise figures, which this screen never showed. Every number here is
        * already on the rows below; a platform stats page whose first question is *how is the
        * platform doing* had no answer to it, and the owner counted that against the whole product.
        *
        * **Summed only over rows whose figures are real** - `statsTotals` skips an unreachable
        * tenant rather than adding its initialised zeros, which is the same rule the cells use and
        * for the same reason. The count of rows it could not read is said out loud. */}
      {!isLoading && !isError && rows.length > 0 && (
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3" data-stats-totals>
          <StatCard
            label={tt('Objednávky (30 dní)')}
            value={formatCount(totals.ordersMonth)}
            testId="total-orders-month"
            sub={totals.unreadable > 0 ? unreadableNote : undefined}
            icon={ShoppingBag}
          />
          <StatCard
            label={tt('Tržby (30 dní)')}
            value={formatCents(totals.revenueMonthCents)}
            testId="total-revenue-month"
            sub={totals.unreadable > 0 ? unreadableNote : undefined}
            icon={Wallet}
            color="emerald"
          />
          <StatCard
            label={tt('Zákazníci')}
            value={formatCount(totals.totalCustomers)}
            testId="total-customers"
            sub={totals.unreadable > 0 ? unreadableNote : undefined}
            icon={Users}
          />
        </div>
      )}

      {/* §3.92 - `DataTable`, which the whole Tower used **zero** times while every one of its
        * screens is a list. It brings the three things this hand-rolled table could not do: sort
        * by a column, page, and lay out as cards on a phone. *Which franchise sells most* was not
        * a question this page could answer.
        *
        * `rowAttributes` is what keeps `data-stat-row` and `data-stat-activity` on the row - the
        * component carries that prop for exactly this migration. **It draws each row twice**, a
        * `<tr>` and a card, so a spec counting rows is scoped to the table
        * (`agos-datatable-renders-every-row-twice`). */}
      {!isLoading && !isError && (
        <DataTable<FranchiseStats>
          columns={columns}
          data={rows}
          rowKey={(row) => row.slug}
          rowAttributes={(row) => ({
            'data-stat-row': row.slug,
            'data-stat-activity': activityState(row, now),
          })}
          emptyMessage={tt('Žiadne prevádzky.')}
          compact
        />
      )}
    </main>
  )
}
